import content, { format, countLabel } from "../site/content";
import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { gites, gitesById, giteName, gitePath, nightsBetween, stayPrice, money, today, maxGuests } from '../site/data';
import { validateStay, contactPayload } from '../site/contact';
import { TextLines, PageTitle, Icon, SeaPicture, ButtonLink } from '../site/UI';
import ProtectedEmail from '../site/ProtectedEmail';
function ContactForm({
  params
}) {
  const selected = gitesById[params.get('gite')];
  const initialPeople = Number(params.get('personnes')) || content.pricing.defaultGuests;
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    message: '',
    gite: selected?.initial || '',
    arrival: params.get('arrivee') || '',
    departure: params.get('depart') || '',
    people: Math.max(1, Math.min(initialPeople, selected?.maxclient || maxGuests)),
    preference: content.pages.contact.form.preferences[0].value
  });
  const [state, setState] = useState({
    status: 'idle'
  });
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const gite = gitesById[fields.gite];
  const nights = nightsBetween(fields.arrival, fields.departure);
  function change(event) {
    const {
      name,
      value
    } = event.target;
    setFields(previous => ({
      ...previous,
      [name]: value,
      ...(name === 'gite' ? {
        people: Math.min(Number(previous.people), gitesById[value]?.maxclient || maxGuests)
      } : {})
    }));
    if (state.status !== 'idle') setState({
      status: 'idle'
    });
  }
  async function submit(event) {
    event.preventDefault();
    if (state.status === 'sending') return;
    const error = validateStay(gite, fields.arrival, fields.departure, Number(fields.people));
    if (error) {
      setState({
        status: 'error',
        message: error
      });
      return;
    }
    if (![fields.firstName, fields.lastName, fields.email, fields.message].every(value => value.trim())) {
      setState({
        status: 'error',
        message: content.pages.contact.form.errors.required
      });
      return;
    }
    setState({
      status: 'sending'
    });
    controller.current = new AbortController();
    const timer = setTimeout(() => controller.current?.abort(), 30000);
    try {
      const response = await fetch('/api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.current.signal,
        body: JSON.stringify(contactPayload(fields, gite))
      });
      const result = await response.json();
      if (!response.ok || result.status !== 'OK') throw new Error('send-failed');
      setState({
        status: 'success',
        key: result.redis_key
      });
    } catch {
      setState({
        status: 'error',
        message: content.pages.contact.form.errors.send
      });
    } finally {
      clearTimeout(timer);
    }
  }
  return <form className="contact-form" onSubmit={submit} aria-busy={state.status === 'sending'}>
    <h2>{content.pages.contact.form.title}</h2><p className="form-intro">{content.pages.contact.form.required}</p>
    <fieldset disabled={typeof window === 'undefined' || state.status === 'sending' || state.status === 'success'}>
      <div className="form-grid">
        <label>{content.pages.contact.form.fields.firstName}<input name="firstName" autoComplete="given-name" required value={fields.firstName} onChange={change} /></label>
        <label>{content.pages.contact.form.fields.lastName}<input name="lastName" autoComplete="family-name" required value={fields.lastName} onChange={change} /></label>
        <label>{content.pages.contact.form.fields.email}<input name="email" type="email" autoComplete="email" required value={fields.email} onChange={change} /></label>
        <label>{content.pages.contact.form.fields.phone}<input name="phone" type="tel" autoComplete="tel" value={fields.phone} onChange={change} /></label>
        <label className="full-width">{content.pages.contact.form.fields.gite}<select aria-label={content.pages.contact.form.fields.gite} name="gite" required value={fields.gite} onChange={change}><option value="">{content.pages.contact.form.chooseGite}</option>{gites.map(item => <option key={item.initial} value={item.initial}>{format(content.pages.contact.form.giteOption, {
                name: giteName(item),
                count: item.maxclient
              })}</option>)}</select></label>
        <label>{content.pages.contact.form.fields.arrival}<input name="arrival" type="date" required min={today()} value={fields.arrival} onChange={change} /></label>
        <label>{content.pages.contact.form.fields.departure}<input name="departure" type="date" required min={fields.arrival || today()} value={fields.departure} onChange={change} /></label>
        <label>{content.pages.contact.form.fields.people}<select aria-label={content.pages.contact.form.fields.people} name="people" value={fields.people} onChange={change}>{Array.from({
              length: gite?.maxclient || maxGuests
            }, (_, i) => <option key={i} value={i + 1}>{countLabel('people', i + 1)}</option>)}</select></label>
        <label>{content.pages.contact.form.fields.preference}<select aria-label={content.pages.contact.form.fields.preference} name="preference" value={fields.preference} onChange={change}><>{content.pages.contact.form.preferences.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}</></select></label>
        <label className="full-width">{content.pages.contact.form.fields.message}<textarea name="message" required rows="4" placeholder={content.pages.contact.form.messagePlaceholder} value={fields.message} onChange={change} /></label>
      </div>
      <details className="optional-address"><summary>{content.pages.contact.form.addAddress}</summary><label>{content.pages.contact.form.fields.address}<textarea name="address" autoComplete="street-address" rows="2" value={fields.address} onChange={change} /></label></details>
      {gite && nights > 0 && <p className="contact-estimate" aria-live="polite">{format(content.pages.contact.form.estimate, {
          nights: countLabel('nights', nights),
          people: countLabel('people', fields.people)
        })} <strong>{money(stayPrice(gite, nights, Number(fields.people)))}</strong><small>{content.pages.contact.form.estimateNote}</small></p>}
      <button className="button" type="submit">{state.status === 'sending' ? content.pages.contact.form.sending : content.pages.contact.form.send}<Icon name="arrow" /></button>
    </fieldset>
    <div aria-live="polite">{state.status === 'success' && <div className="form-success"><h3>{content.pages.contact.form.success}</h3><p>{content.pages.contact.form.successNote}</p>{state.key && <><a className="text-link" href={'/contrat?key=' + encodeURIComponent(state.key)} download>{content.pages.contact.form.download}{" "}<Icon name="arrow" /></a><p>{content.pages.contact.form.returnContract}{" "}<ProtectedEmail showIcon={false} />. {content.pages.contact.form.contractExpiry}</p></>}</div>}</div>
    {state.status === 'error' && <p className="form-error" role="alert">{state.message}</p>}
    <p className="form-note centered">{content.pages.contact.form.confirmation}</p>
  </form>;
}
export default function PageContact() {
  const [params] = useSearchParams();
  const addresses = [...new Set(gites.map(gite => gite.adresse))];
  return <div className="container section contact-page">
    <PageTitle title={content.pages.contact.meta.title} description={content.pages.contact.meta.description} />
    <header className="page-intro centered"><p className="eyebrow">{content.pages.contact.eyebrow}</p><h1>{content.pages.contact.title}</h1><p className="lead">{content.pages.contact.intro}</p><span className="gold-rule" /></header>
    <div className="contact-layout"><aside className="contact-info"><SeaPicture eager /><h2><TextLines lines={content.pages.contact.directTitle} /></h2><ProtectedEmail /><a href={content.site.phone.href}><Icon name="phone" />{content.site.phone.label}</a><p><Icon name="pin" />{content.site.location}</p><span className="gold-rule" /><p className="contact-aside-note">{content.pages.contact.advice}</p></aside><ContactForm key={params.toString()} params={params} /></div>
    <section id="acces" className="contact-access"><h2>{content.pages.contact.access.title}</h2><div className="contact-access-grid">{addresses.map(address => {
      const properties = gites.filter(gite => gite.adresse === address);
      return <div key={address}><h3>{address}</h3><ul>{properties.map(gite => <li key={gite.initial}><Link className="text-link" to={gitePath(gite)}>{giteName(gite)}</Link></li>)}</ul><a className="text-link" href={properties[0].lieu} target="_blank" rel="noreferrer">{content.pages.property.location.directions}<Icon name="arrow" /></a></div>;
    })}</div></section>
    <section className="contact-bottom"><div><h2>{content.pages.contact.bottom.title}</h2><p>{content.pages.contact.bottom.text}</p><div className="button-row"><ButtonLink to="/#nos-gites" outline>{content.common.discoverGites}</ButtonLink><ButtonLink to="/nos-tarifs" outline>{content.common.viewRates}</ButtonLink></div></div><SeaPicture /></section>
  </div>;
}
